// Shared session-status primitive used by both the user-side
// AuthProvider and the admin-side AdminAuthProvider (task p_e2e_boat_specs,
// ADR 0042).
//
// What this hook owns:
//   - tri-state status (unknown | authenticated | anonymous)
//   - the cached "session record" object (whatever the probe returns)
//   - one whoami probe on mount + a refresh() to re-probe
//   - a logout() that posts to a configurable endpoint and clears
//     local state regardless of the network outcome
//
// What this hook does NOT own:
//   - login() — the user-side has a password login that returns the
//     same record-shape; the admin-side has no login at all (it goes
//     through Google OIDC + TOTP redirects). Each provider composes
//     login on top.
//   - context wiring — providers wrap this hook in their own
//     React Context so consumers get a stable API surface
//     (`useAuth()` / `useAdminAuth()`).
//
// Keeping the two contexts disjoint is intentional (ADR 0040): admin
// and user sessions are different security domains. This hook just
// removes the plumbing duplication between them.

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api';

export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous';

export interface SessionState<TRecord> {
  status: SessionStatus;
  record: TRecord | null;
  /** Re-probe the session; resolves to the resulting status (never rejects). */
  refresh: () => Promise<SessionStatus>;
  logout: () => Promise<void>;
  setRecord: (record: TRecord | null) => void;
  setStatus: (status: SessionStatus) => void;
}

interface UseSessionOptions<TRecord> {
  /** GET endpoint that returns the session record, or 401 if anonymous. */
  whoamiUrl: string;
  /** POST endpoint that revokes the session. */
  logoutUrl: string;
  /** Optional projection: api() returns `unknown`; map it to TRecord. */
  parseRecord?: (raw: unknown) => TRecord;
}

export function useSession<TRecord>(opts: UseSessionOptions<TRecord>): SessionState<TRecord> {
  const [status, setStatus] = useState<SessionStatus>('unknown');
  const [record, setRecord] = useState<TRecord | null>(null);

  // Callers pass a fresh options object (with an inline parseRecord) on every
  // render. Holding the latest opts in a ref keeps refresh()/logout() stable,
  // so the mount probe runs exactly once instead of re-firing on every render
  // — which, for an authenticated session (setRecord never bails on a fresh
  // object), would otherwise re-probe whoamiUrl in an endless loop.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  // Monotonic probe id. When probes overlap — the mount probe still in flight
  // as login()/refresh() fires another — only the most recently started probe
  // may commit its result. Without this a slow earlier probe (e.g. the
  // pre-login 401) could resolve last and overwrite a freshly-authenticated
  // session back to 'anonymous', bouncing the user to /login.
  const probeGen = useRef(0);

  const refresh = useCallback(async (): Promise<SessionStatus> => {
    const { whoamiUrl, parseRecord } = optsRef.current;
    const gen = ++probeGen.current;
    const superseded = (): boolean => gen !== probeGen.current;
    try {
      // api() renews an aged access cookie underneath this call when the app
      // has enabled renewal (task remember_me), so a 401 arriving here really
      // does mean signed out.
      const raw = await api(whoamiUrl);
      // A newer probe started while this one was in flight — let it win.
      if (superseded()) return 'authenticated';
      const parsed = parseRecord ? parseRecord(raw) : (raw as TRecord);
      setRecord(parsed);
      setStatus('authenticated');
      return 'authenticated';
    } catch (err) {
      if (superseded()) return 'anonymous';
      const e = err as { status?: number };
      // 401 → definitively signed out, drop any stale record. Network error
      // or 5xx → fall back to anonymous (keep the stale record) so subsequent
      // interactions re-probe rather than freezing the UI in 'unknown'.
      if (e.status === 401) {
        setRecord(null);
      }
      setStatus('anonymous');
      return 'anonymous';
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    // Supersede any in-flight probe so a pre-logout /v1/me that resolves
    // 'authenticated' after this can't re-auth the UI past a sign-out.
    probeGen.current++;
    try {
      await api(optsRef.current.logoutUrl, { method: 'POST' });
    } finally {
      setRecord(null);
      setStatus('anonymous');
    }
  }, []);

  return { status, record, refresh, logout, setRecord, setStatus };
}
